import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { authSelectors } from '../../store/selectors/auth';
import { testSelectors } from '../../store/selectors/test';
import { checkTestThunk, continueTestThunk } from '../../store/thunks/test';
import actions from '../../store/actions';
import Button from '../../common/Buttons/Button';
import TestAnswer from './TestAnswer';
import { leaveTestStage } from '../../utils/testStage';
import usePreviewFeature from '../../hooks/usePreviewFeature';

const NEXT_DELAY_CORRECT_MS = 700;
const NEXT_DELAY_WRONG_MS = 1500;
const PREVIEW_DELAY_CORRECT_MS = 1600;
const PREVIEW_DELAY_WRONG_MS = 2800;

const TestTypeAnswers = ({ setOpenModal }) => {
  const dispatch = useDispatch();

  const auth = useSelector(authSelectors);
  const token = auth.token;
  const test = useSelector(testSelectors);
  const testInfo = test.test;
  const answers = test.answers;
  const error = test.error;
  const check = test.check;
  const loadBtn = test.loadBtn;
  const isRedesign = usePreviewFeature('courses-redesign');

  const [isAnswered, setIsAnswered] = useState(false);
  const [chosenAnswerId, setChosenAnswerId] = useState(null);

  const submittedRef = useRef(false);
  const nextSentRef = useRef(false);
  const hasCheckResult = check && Object.keys(check).length > 0;
  const isChecked = isAnswered && hasCheckResult;

  useEffect(() => {
    submittedRef.current = false;
    nextSentRef.current = false;
    setIsAnswered(false);
    setChosenAnswerId(null);
    dispatch(actions.test.checkTest({}));
  }, [testInfo]);

  useEffect(() => {
    if (!error) return;

    nextSentRef.current = false;

    if (!hasCheckResult) {
      submittedRef.current = false;
      setIsAnswered(false);
    }
  }, [error]);

  const setNextTest = () => {
    if (nextSentRef.current) return;
    nextSentRef.current = true;

    const id = testInfo.el_homework_block_id;

    leaveTestStage();
    dispatch(actions.test.getTestError(''));
    localStorage.setItem('currentTestId', JSON.stringify(id));
    dispatch(continueTestThunk({ id, token }));
  };

  useEffect(() => {
    if (!isChecked) return undefined;

    const isRight = check?.correct_answer_id === chosenAnswerId;
    const delay = isRedesign
      ? (isRight ? PREVIEW_DELAY_CORRECT_MS : PREVIEW_DELAY_WRONG_MS)
      : (isRight ? NEXT_DELAY_CORRECT_MS : NEXT_DELAY_WRONG_MS);
    const timer = setTimeout(setNextTest, delay);

    return () => clearTimeout(timer);
  }, [isChecked]);

  const submitAnswer = (answerId = chosenAnswerId) => {
    if (submittedRef.current || isAnswered || answerId === null) return;
    submittedRef.current = true;

    const data = {
      blockId: testInfo.el_homework_block_id,
      taskId: testInfo.homework_task_id,
      data: { answer_id: answerId },
      token,
    };

    dispatch(actions.test.getTestError(''));
    dispatch(checkTestThunk(data));
    setIsAnswered(true);
  };

  const handleChooseAnswer = (id) => {
    if (isAnswered) return;

    if (isRedesign && chosenAnswerId === id) {
      submitAnswer(id);

      return;
    }

    setChosenAnswerId(id);
  };

  const setTestModal = () => {
    setOpenModal(true);
  };

  return (
    <>
      <div>
        <div className="test__answers">
          {answers.length > 0 && answers.map((answer, idx) => {
            const isChosen = chosenAnswerId === answer.id;
            const isCorrect = check?.correct_answer_id === answer.id;

            return (
              <TestAnswer
                key={answer.id}
                count={idx + 1}
                answer={answer.answer}
                choosen={isChosen}
                correct={isChecked && isCorrect}
                incorrect={isChecked && isChosen && !isCorrect}
                disabled={isAnswered}
                lang={testInfo.tts_lang}
                onClick={!isAnswered ? () => handleChooseAnswer(answer.id) : undefined}
              />
            );
          })}
        </div>

        {error && <div className="test__error error-message">{error}</div>}
      </div>
      <div className="test__btns">
        {isChecked
          ? <Button buttonBg='green' textSize='md' btnRadius='md' disabled={loadBtn}
            onClick={setNextTest}>
            Далі
          </Button>
          : <Button buttonBg='green' textSize='md' btnRadius='md'
            disabled={loadBtn || isAnswered || chosenAnswerId === null}
            onClick={() => submitAnswer()}>
            Перевірити
          </Button>
        }
        <Button buttonBg='light' iconSvg='flag' iconColor='violet' onClick={() => setTestModal()}>
          Повідомити про помилку
        </Button>
      </div>
    </>
  );
};

TestTypeAnswers.propTypes = {
  setOpenModal: PropTypes.func,
};

export default TestTypeAnswers;
